import React, { useCallback, useEffect, useState, useRef, Children, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { EmailGate } from './components/EmailGate';
// --- Slide Data ---
const slides = [{
  id: 1,
  heading: 'OpenFPGA.ai',
  isTitle: true,
  bullets: ['The FPGA inference cloud']
}, {
  id: 9,
  label: 'Team',
  heading: 'Built for hardware leverage and company building',
  isTeam: true,
  team: [{
    name: 'Orion',
    role: 'CEO',
    bio: 'YC alum (S16), Founding GP of orangedao.xyz, the community of YC-backed founders. Deployed $26M into 200 web3 startups, achieving 2.2X MOIC at Y3. MBA, MSEE with patent for a portable scalable signal processor.',
    initials: 'O',
    photo: 'https://cdn.magicpatterns.com/uploads/7DzT6U6JDVy9Cx3yC7puz7/OP_sq.png',
    imgClass: 'grayscale'
  }, {
    name: 'Ezra Wolf',
    role: 'CTO',
    bio: 'Chip designer with 3 silicon tapeouts, including a satellite that launched to the ISS in April 2026. Built digital CIM macros in 28nm and analog CIM in 130nm at 50x H100 efficiency. Designing compute-in-memory since age 14. EE, finishing December 2026.',
    initials: 'E',
    photo: 'https://cdn.magicpatterns.com/uploads/n19NN1wzp3KbrqaKnwiNvp/pasted-image.jpg',
    imgClass: 'scale-125'
  }]
}, {
  id: 2,
  label: 'Problem',
  heading: 'GPUs won AI through ubiquity and CUDA.',
  bullets: ['FPGAs beat GPUs on inference workloads', "But AI teams shouldn't need bare metal expertise."]
}, {
  id: 3,
  label: 'Solution',
  heading: 'Devs swap GPU for FPGA inference with a single API endpoint.',
  bullets: ['We handle tuning, deployment, orchestration', '3x faster, 5x more efficient = 15x better performance']
}, {
  id: 6,
  label: 'Insight',
  heading: 'The bottleneck is usability, not hardware potential',
  bullets: ['Cloud GPUs proved the interface matters', 'We bring that interface to FPGAs']
}, {
  id: 7,
  label: 'Business Model',
  heading: 'Usage-based inference revenue',
  bullets: ['Software model on top of hardware advantage', 'High-value workloads first']
}, {
  id: 8,
  label: 'Market',
  heading: 'Beachhead: latency-sensitive LLM workloads',
  bullets: ['Expand into edge and real-time serving', 'Start where GPU pain is highest']
}, {
  id: 10,
  label: 'Fundraise',
  heading: 'Raising $1M',
  bullets: ['Expand model and workload support', 'Scale infra, deployment, and onboarding', 'Reach $4M ARR'],
  closingLine: "We're giving FPGAs their CUDA moment."
}];
// --- Animation Variants ---
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
    scale: 0.98
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      x: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      },
      opacity: {
        duration: 0.4
      },
      scale: {
        duration: 0.4
      }
    }
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    scale: 0.98,
    transition: {
      x: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      },
      opacity: {
        duration: 0.4
      },
      scale: {
        duration: 0.4
      }
    }
  })
};
const contentVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 400,
      damping: 30
    }
  }
};
export function App() {
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isDirectMode = new URLSearchParams(window.location.search).get('mode') === 'direct';
  useEffect(() => {
    const unlocked = localStorage.getItem('openfpga_email_unlocked') === 'true';
    setIsUnlocked(unlocked);
  }, []);
  const paginate = useCallback((newDirection: number) => {
    setPage(prevPage => {
      const nextPage = prevPage + newDirection;
      if (nextPage < 0 || nextPage >= slides.length) return prevPage;
      // Gate check
      if (!isUnlocked && nextPage > 0) {
        setShowGate(true);
        return prevPage;
      }
      setDirection(newDirection);
      return nextPage;
    });
  }, [isUnlocked]);
  const goToSlide = useCallback((index: number) => {
    if (index < 0 || index >= slides.length || index === page) return;
    // Gate check
    if (!isUnlocked && index > 0) {
      setShowGate(true);
      return;
    }
    setDirection(index > page ? 1 : -1);
    setPage(index);
  }, [page, isUnlocked]);
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (showGate) {
          setShowGate(false);
        } else {
          paginate(-1);
        }
      } else if (e.key === 'Escape' && showGate) {
        setShowGate(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate, showGate]);
  // Touch navigation
  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;
      const dx = e.changedTouches[0].clientX - touchStartX.current;
      const dy = e.changedTouches[0].clientY - touchStartY.current;
      // Only treat as horizontal swipe if horizontal movement dominates
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) {
        // Swipe left → next
        paginate(1);
      } else {
        // Swipe right → prev or close gate
        if (showGate) {
          setShowGate(false);
        } else {
          paginate(-1);
        }
      }
      touchStartX.current = null;
      touchStartY.current = null;
    };
    window.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    window.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [paginate, showGate]);
  const currentSlide = slides[page];
  return <div className="relative w-screen min-h-screen md:h-screen bg-dark md:overflow-hidden font-inter text-white selection:bg-cyan selection:text-dark" data-id="element-0">
      <EmailGate isOpen={showGate} onClose={() => setShowGate(false)} requireOtp={!isDirectMode} onUnlock={() => {
      setIsUnlocked(true);
      setShowGate(false);
      // Automatically advance to the next slide after unlock
      if (page === 0) {
        setDirection(1);
        setPage(1);
      }
    }} data-id="element-1" />

      {/* Background Effects */}
      <div className="absolute inset-0 bg-tech-grid opacity-40 pointer-events-none" data-id="element-2"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,#0a0a0f_100%)] opacity-80 pointer-events-none" data-id="element-3"></div>
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan to-transparent opacity-20 pointer-events-none" data-id="element-4"></div>

      {/* Slide Content */}
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-5">
        <motion.div key={page} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex flex-col justify-center px-6 py-20 md:py-0 md:px-24 lg:px-40 overflow-y-auto md:overflow-hidden" data-id="element-6">
          <motion.div variants={contentVariants} initial="hidden" animate="show" className="max-w-6xl w-full mx-auto" data-id="element-7">
            {currentSlide.isTeam ? null : currentSlide.isTitle ? <div className="flex flex-col items-start space-y-6" data-id="element-8">
                <motion.div variants={itemVariants} className="inline-block mb-4" data-id="element-9">
                  <div className="h-1 w-24 bg-cyan mb-8" data-id="element-10"></div>
                </motion.div>
                <motion.h1 variants={itemVariants} className="font-space text-5xl md:text-8xl lg:text-9xl font-bold tracking-tighter text-glow" data-id="element-11">
                  {currentSlide.heading}
                </motion.h1>
                <motion.ul variants={contentVariants} initial="hidden" animate="show" className="space-y-4 mt-8" data-id="element-12">
                  {currentSlide.bullets?.map((bullet, idx) => <motion.li key={idx} variants={itemVariants} className="text-lg md:text-3xl font-light text-gray-300" data-id="element-13">
                      <span className="text-cyan mr-4" data-id="element-14">—</span>
                      {bullet}
                    </motion.li>)}
                </motion.ul>
              </div> : <div className={`flex ${currentSlide.image ? 'flex-col md:flex-row md:items-center md:gap-12' : 'flex-col'} items-start`} data-id="element-15">
                <div className={currentSlide.image ? 'flex-1' : 'w-full'} data-id="element-16">
                  {currentSlide.label && <motion.div variants={itemVariants} className="mb-6" data-id="element-17">
                      <span className="text-cyan text-xs md:text-base font-space font-medium tracking-[0.2em] uppercase" data-id="element-18">
                        {currentSlide.label}
                      </span>
                    </motion.div>}
                  <motion.div variants={itemVariants} className={currentSlide.image ? 'mb-8 md:mb-10' : 'mb-10 md:mb-16'} data-id="element-19">
                    <h2 className={`font-space font-bold tracking-tight leading-tight ${currentSlide.image ? 'text-3xl md:text-4xl lg:text-5xl' : 'text-3xl md:text-5xl lg:text-7xl'}`} data-id="element-20">
                      {currentSlide.heading}
                    </h2>
                    <div className="h-1 w-16 md:w-24 bg-cyan mt-6 opacity-70" data-id="element-21"></div>
                  </motion.div>
                  <motion.ul variants={contentVariants} className={`w-full ${currentSlide.image ? 'space-y-4 md:space-y-8' : 'space-y-6 md:space-y-12'}`} data-id="element-22">
                    {currentSlide.bullets?.map((bullet, idx) => <motion.li key={idx} variants={itemVariants} className={`flex items-start font-light text-gray-200 ${currentSlide.image ? 'text-lg md:text-2xl' : 'text-xl md:text-4xl'}`} data-id="element-23">
                        <span className={`text-cyan mr-4 md:mr-6 mt-1 md:mt-2 ${currentSlide.image ? 'text-sm md:text-lg' : 'text-lg md:text-2xl'}`} data-id="element-24">
                          ■
                        </span>
                        <span className="leading-snug" data-id="element-25">{bullet}</span>
                      </motion.li>)}
                  </motion.ul>
                  {currentSlide.closingLine && <motion.p variants={itemVariants} className="mt-12 md:mt-16 text-xl md:text-4xl text-cyan font-space font-bold text-glow" data-id="element-26">
                      {currentSlide.closingLine}
                    </motion.p>}
                </div>
                {currentSlide.image && <motion.div variants={itemVariants} className="flex-shrink-0 flex items-center justify-center md:justify-end mt-10 md:mt-0 w-full md:w-[35%]" data-id="element-27">
                    <img src={currentSlide.image} alt="Slide visual" className="max-h-[30vh] md:max-h-[50vh] w-auto rounded-lg border border-white/10 shadow-[0_0_30px_rgba(0,212,255,0.1)]" data-id="element-28" />
                  </motion.div>}
              </div>}

            {/* Team Slide */}
            {currentSlide.isTeam && <div className="flex flex-col items-start" data-id="element-29">
                {currentSlide.label && <motion.div variants={itemVariants} className="mb-4" data-id="element-30">
                    <span className="text-cyan text-xs md:text-base font-space font-medium tracking-[0.2em] uppercase" data-id="element-31">
                      {currentSlide.label}
                    </span>
                  </motion.div>}
                <motion.div variants={itemVariants} className="mb-8 md:mb-10" data-id="element-32">
                  <h2 className="font-space text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" data-id="element-33">
                    {currentSlide.heading}
                  </h2>
                  <div className="h-1 w-16 md:w-24 bg-cyan mt-6 opacity-70" data-id="element-34"></div>
                </motion.div>
                <motion.div variants={contentVariants} className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10 w-full" data-id="element-35">
                  {currentSlide.team?.map((member, idx) => <motion.div key={idx} variants={itemVariants} className="flex flex-col items-start" data-id="element-36">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-cyan/10 border-2 border-cyan/30 overflow-hidden mb-4" data-id="element-37">
                        {member.photo ? <img src={member.photo} alt={member.name} className={`w-full h-full object-cover ${(member as any).imgClass || ''}`} data-id="element-38" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-39">
                            <span className="font-space text-cyan text-xl md:text-2xl font-bold" data-id="element-40">
                              {member.initials}
                            </span>
                          </div>}
                      </div>
                      <h3 className="font-space text-2xl md:text-3xl font-bold text-white" data-id="element-41">
                        {member.name}
                      </h3>
                      <p className="text-cyan font-space text-xs md:text-sm font-medium tracking-wider uppercase mt-1 mb-3 md:mb-4" data-id="element-42">
                        {member.role}
                      </p>
                      <p className="text-gray-400 text-sm md:text-base leading-relaxed font-light" data-id="element-43">
                        {member.bio}
                      </p>
                    </motion.div>)}
                </motion.div>
              </div>}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Footer / Navigation */}
      <div className="absolute bottom-6 md:bottom-8 left-0 w-full px-6 md:px-24 flex justify-between items-center z-50 pointer-events-none" data-id="element-44">
        <div className="font-space text-xs md:text-sm text-gray-500 tracking-widest uppercase hidden md:block" data-id="element-45">
          OpenFPGA.ai
        </div>

        {/* Dots */}
        <div className="flex space-x-2 md:space-x-3 pointer-events-auto mx-auto md:mx-0" data-id="element-46">
          {slides.map((_, idx) => <button key={idx} onClick={() => goToSlide(idx)} className={`w-2 h-2 rounded-full transition-all duration-300 ${idx === page ? 'bg-cyan w-6 md:w-8 shadow-[0_0_10px_rgba(0,212,255,0.8)]' : 'bg-gray-700 hover:bg-gray-500'}`} aria-label={`Go to slide ${idx + 1}`} data-id="element-47" />)}
        </div>

        <div className="font-space text-xs md:text-sm text-gray-500 tabular-nums hidden md:block" data-id="element-48">
          {page + 1} / {slides.length}
        </div>
      </div>

      {/* Invisible click areas for mouse navigation */}
      <div className="absolute top-0 left-0 w-1/4 h-full z-40 cursor-w-resize hidden md:block" onClick={() => paginate(-1)} data-id="element-49" />
      <div className="absolute top-0 right-0 w-1/4 h-full z-40 cursor-e-resize hidden md:block" onClick={() => paginate(1)} data-id="element-50" />
    </div>;
}